import React, { useState, useEffect } from "react"
import { X } from "lucide-react"

const EditIdealCandidateModal = ({
  isOpen,
  onClose,
  currentProfile,
  onSave,
  isLoading,
}) => {
  const [profileText, setProfileText] = useState("")

  useEffect(() => {
    if (isOpen && currentProfile) {
      setProfileText(currentProfile)
    }
  }, [isOpen, currentProfile])

  const handleSave = () => {
    onSave(profileText)
  }

  const handleCancel = () => {
    setProfileText(currentProfile)
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        
        .edit-modal-container {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }
      `}</style>

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm edit-modal-container">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 border border-slate-200">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-slate-200">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 tracking-tight">
                Edit Ideal Candidate Profile
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Update the requirements for your ideal candidate
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="text-slate-400 hover:text-slate-600 transition-colors p-1 hover:bg-slate-100 rounded-lg"
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-8 py-6">
            <label className="block text-sm font-semibold text-slate-900 mb-3">
              Profile Description
            </label>
            <textarea
              value={profileText}
              onChange={(e) => setProfileText(e.target.value)}
              placeholder="Describe your ideal candidate profile including skills, experience, qualifications, and key attributes..."
              className="w-full h-80 px-4 py-3 border border-slate-300 rounded-lg text-[15px] text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none leading-relaxed"
              disabled={isLoading}
            />
            <div className="mt-3 flex items-start gap-2">
              <svg
                className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-slate-500 leading-relaxed">
                Be specific about technical skills, years of experience,
                educational background, and soft skills that would make a
                candidate ideal for this role.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-slate-200 bg-slate-50">
            <button
              onClick={handleCancel}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !profileText.trim()}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default EditIdealCandidateModal
